import { AudioLinesIcon, ImageIcon, LinkIcon, SplitIcon, VideoIcon } from "lucide-react";
import { LabelList, Pie, PieChart, ResponsiveContainer, Cell } from "recharts";
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import React, { useState, useEffect } from "react";
import { FirmDashboard } from "@/lib/types";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";

// Animation variants
const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 80,
      damping: 15,
      when: "beforeChildren",
      staggerChildren: 0.1,
    },
  },
};

const titleVariants = {
  hidden: { opacity: 0, y: -10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 200,
      damping: 20,
    },
  },
};

const contentVariants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 15,
      delay: 0.2,
    },
  },
};

const legendItemVariants = {
  hidden: { opacity: 0, x: -10 },
  visible: (custom: number) => ({
    opacity: 1,
    x: 0,
    transition: {
      type: "spring",
      stiffness: 200,
      damping: 20,
      delay: 0.3 + custom * 0.1,
    },
  }),
};

export const ContentsPieChart: React.FC<{ dasboard: FirmDashboard }> = ({ dasboard }) => {
  const { t } = useTranslation();
  const [animateChart, setAnimateChart] = useState(false);

  // Start animation after component mounts
  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimateChart(true);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  const chartData = [
    { contentType: "image", count: dasboard.totalContentImg, fill: "var(--color-image)" },
    { contentType: "video", count: dasboard.totalContentVideo, fill: "var(--color-video)" },
    { contentType: "link", count: dasboard.totalContentLink, fill: "var(--color-link)" },
    { contentType: "split", count: dasboard.totalContentSplit, fill: "var(--color-split)" },
    { contentType: "audio", count: dasboard.totalContentAudio, fill: "var(--color-audio)" },
    // TODO add coloring for other types
  ];

  // Calculate total for percentage calculation
  const total = chartData.reduce((sum, item) => sum + item.count, 0);

  // Add percentage to each item
  const chartDataWithPercentage = chartData.map((item) => ({
    ...item,
    percentage: (item.count / total) * 100,
  }));

  const chartConfig = {
    count: {
      label: "count",
    },
    image: {
      label: t("content.image"),
      color: "hsl(var(--chart-1))",
      icon: ImageIcon,
    },
    video: {
      label: t("content.video"),
      color: "hsl(var(--chart-2))",
      icon: VideoIcon,
    },
    link: {
      label: t("content.link"),
      color: "hsl(var(--chart-3))",
      icon: LinkIcon,
    },
    split: {
      label: t("entities.splitContent"),
      color: "hsl(var(--chart-4))",
      icon: SplitIcon,
    },
    audio: {
      label: t("content.audio"),
      color: "hsl(var(--chart-5))",
      icon: AudioLinesIcon,
    },
  } satisfies ChartConfig;

  // Custom colors for the pie chart
  const COLORS = ["#4F46E5", "#0EA5E9", "#10B981", "#F59E0B", "#EC4899"];

  return (
    <motion.div variants={cardVariants} initial="hidden" animate="visible">
      <Card className="flex flex-col border-none shadow-sm bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-950/50 dark:to-slate-900/30">
        <CardHeader className="items-center pb-0">
          <motion.div variants={titleVariants}>
            <CardTitle className="text-base font-medium">{t("dashboard.contents")}</CardTitle>
            <CardDescription className="text-center text-xs">{t("dashboard.contentsDesc")}</CardDescription>
          </motion.div>
        </CardHeader>
        <CardContent className="flex-1 pb-0">
          <motion.div variants={contentVariants} className="w-full">
            <ResponsiveContainer width="100%" height={250}>
              <ChartContainer
                config={chartConfig}
                className="mx-auto aspect-square max-h-[250px] [&_.recharts-text]:fill-background"
              >
                <PieChart>
                  <ChartTooltip
                    content={
                      <ChartTooltipContent
                        nameKey="contentType"
                        formatter={(_value: any, _name: any, props: any) => {
                          const item = props.payload;
                          return [`${item.percentage.toFixed(1)}% (${item.count})`];
                        }}
                      />
                    }
                  />
                  <Pie
                    data={chartDataWithPercentage}
                    dataKey="count"
                    nameKey="contentType"
                    cx="50%"
                    cy="50%"
                    innerRadius={animateChart ? 60 : 0}
                    outerRadius={animateChart ? 80 : 40}
                    paddingAngle={animateChart ? 2 : 0}
                    cornerRadius={animateChart ? 4 : 0}
                    animationDuration={1500}
                    animationBegin={0}
                    animationEasing="ease"
                  >
                    {chartDataWithPercentage.map((_entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                    <LabelList
                      dataKey="contentType"
                      position="outside"
                      className="fill-foreground"
                      stroke="none"
                      fontSize={12}
                      formatter={(value: keyof typeof chartConfig) => {
                        const percentage = chartDataWithPercentage.find(
                          (item) => item.contentType === value
                        )?.percentage;
                        // Show label if percentage is greater than 5
                        return percentage && percentage >= 5 ? chartConfig[value]?.label : "";
                      }}
                    />
                  </Pie>
                </PieChart>
              </ChartContainer>
            </ResponsiveContainer>
          </motion.div>
        </CardContent>
        <CardFooter className="flex justify-center gap-4 pb-4">
          <AnimatePresence>
            {chartDataWithPercentage.map((item, index) => (
              <motion.div
                key={item.contentType}
                className="flex items-center gap-1"
                custom={index}
                variants={legendItemVariants}
                whileHover={{
                  scale: 1.1,
                  transition: { duration: 0.2 },
                }}
              >
                <motion.div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: COLORS[index % COLORS.length] }}
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.5 + index * 0.1, type: "spring", stiffness: 500 }}
                />
                <span className="text-xs">{chartConfig[item.contentType as keyof typeof chartConfig]?.label}</span>
              </motion.div>
            ))}
          </AnimatePresence>
        </CardFooter>
      </Card>
    </motion.div>
  );
};
